import { CommonModule, Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { ResizableModule } from 'angular-resizable-element';
import { AddComponent } from 'src/components/add/add.component';
import { CoverComponent } from 'src/components/cover/cover.component';
import { PlannerComponent } from 'src/components/planner/planner.component';
import { SidebarComponent } from 'src/components/sidebar/sidebar.component';
import { DialogService } from 'src/services/dialog/dialog.service';
import { Entry } from 'src/services/entry/Entry';
import { EntryService } from 'src/services/entry/entry.service';
import { DetailedEntry } from 'src/services/entry/EntryWithDetails';
import { Period, Summary, Tag } from 'src/services/tag/Tag';
import { TagTreeMapper } from 'src/services/tag-tree/tag-tree.service';
import { TagNode } from 'src/services/tag-tree/TagNode';
import { TagService } from 'src/services/tag/tag.service';
import { isParent } from 'src/utils/isParent';
import { EntriesComponent } from '../entries/entries.component';
import { LoginComponent } from 'src/components/login/login.component';
import { SettingsComponent } from 'src/components/settings/settings.component';
import { AuthService } from 'src/services/auth/auth.service';
import { applyPreferredTagOrder } from 'src/utils/applyPreferredTagOrder';
import { UserService } from 'src/services/user/user.service';
import { User } from 'src/services/user/User';
import { DetailedTag } from 'src/services/tag/DetailedTag';
import { isDirectParent } from 'src/utils/isDirectParent';
import { isRoot } from 'src/utils/isRoot';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { BrowserService } from 'src/services/browser/browser.service';
import { TagSelectionService } from 'src/services/tag-selection/tag-selection.service';
import { MobilePlannerComponent } from '../mobile-planner/mobile-planner.component';
import { MobileAdderComponent } from '../mobile-adder/mobile-adder.component';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MobileSettingsComponent } from '../../app/components/mobile-settings/mobile-settings.component';
import * as env from 'src/environments/environment';
import * as buildInfo from 'src/environments/buildInfo';
import { SubscriptionSuccessDialogComponent } from 'src/app/components/subscription-success-dialog/subscription-success-dialog.component';
import { RibbonForDebugComponent } from '../../app/components/ribbon-for-debug/ribbon-for-debug.component';
import { RibbonForDemoComponent } from 'src/app/components/ribbon-for-demo/ribbon-for-demo.component';
import { RibbonForEmailVerificationComponent } from '../../app/components/ribbon-for-email-verification/ribbon-for-email-verification.component';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LocalSettingsService } from 'src/app/services/local-settings.service';

@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  imports: [
    ResizableModule,
    SidebarComponent,
    CoverComponent,
    MobileAdderComponent,
    EntriesComponent,
    ResizableModule,
    MatButtonModule,
    MatSnackBarModule,
    MobilePlannerComponent,
    CommonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MobileSettingsComponent,
    RibbonForDebugComponent,
    RibbonForDemoComponent,
    RibbonForEmailVerificationComponent,
  ],

  standalone: true,
  styleUrls: ['./editor.component.scss'],
})
export class EditorComponent implements OnInit {
  tagFullPath: string = '';

  detailedEntries: DetailedEntry[] = [];
  detailedTags: DetailedTag[] = [];
  detailedArchivedTags: DetailedTag[] = [];
  env = env;
  buildInfo = buildInfo;
  tagNodes: TagNode[] = [];

  public isInitialized = false;

  public mainState:
    | 'entries'
    | 'mobile-adder'
    | 'mobile-planner'
    | 'mobile-settings' = 'entries';

  user: User | null = null;
  private tags: Tag[] | null = null;

  private entries: Entry[] | null = null;

  @ViewChild('sidebar') sidebar!: SidebarComponent;
  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('mobileAdder') mobileAdder!: MobileAdderComponent;
  @ViewChild('mobilePlanner') mobilePlanner!: MobilePlannerComponent;

  showCover = this.authService.user === null;
  showDemoRibbon = this.authService.user === null;

  showEmailVerificationRibbon =
    this.authService.user !== null && !this.authService.user?.emailVerified;

  tagPlannedExpenditure: number = 0;

  periodFilter: Period | null = null;

  constructor(
    public authService: AuthService,
    public entryService: EntryService,
    private router: Router,
    private tagService: TagService,
    private _snackBar: MatSnackBar,
    private dialogService: DialogService,
    private userService: UserService,
    public browserService: BrowserService,
    private tagSelectionService: TagSelectionService,
    private changeDetectorRef: ChangeDetectorRef,
    private location: Location,
    public environment: EnvironmentService,
    private localSettingsService: LocalSettingsService
  ) {
    this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationSkipped
      ) {
        this.mainState = 'entries';

        if (
          event.url.includes('/subscription-success') &&
          event instanceof NavigationEnd
        ) {
          const urlParams = new URLSearchParams(
            event.urlAfterRedirects.split('?')[1]
          );
          const sessionId = urlParams.get('session_id');
          if (sessionId) {
            this.openSubscriptionSuccessModal(sessionId);
          }
        }

        if (event.url.includes('/add')) {
          this.openAdder();
        }

        if (event.url.includes('/mobile-planner')) {
          this.mainState = 'mobile-planner';
          this.changeDetectorRef.detectChanges();
        }

        if (event.url.includes('/tags/')) {
          this.tagFullPath = event.url.replace('/tags/', '');
          this.openEntriesWithTag(this.tagFullPath);
        } else if (event.url.includes('/login')) {
          if (!this.authService.user) {
            this.openLogin();
          }
        } else if (event.url.includes('/all')) {
          this.openAllEntries();
        }
      }
    });
  }

  ngOnInit(): void {
    this.localSettingsService.init();

    // User subscription
    this.userService.user?.subscribe((user) => {
      this.user = user;
      this.initializeWithDetailsIfPossible();
    });

    // Tags subscription
    this.tagService.tags.subscribe((tags) => {
      this.tags = tags;
      this.initializeWithDetailsIfPossible();
    });

    // Entries subscription
    this.entryService.entries.subscribe((entries: Entry[]) => {
      this.entries = entries;
      this.initializeWithDetailsIfPossible();
      if (this.isInitialized) {
        setTimeout(() => this.sidebar.hardRefresh(), 0); // :(
      }
    });
  }

  initializeWithDetailsIfPossible() {
    if (this.tags === null || this.user === null || this.entries === null) {
      this.isInitialized = false;
      return;
    } else {
      this.tags = this.addMissingParentTags(this.tags);

      this.orderTags(this.user, this.tags);

      this.mapDetails(this.tags, this.entries);

      // ON MOBILE VIEW THIS IS ANNOYING
      if (
        !!this.authService.user &&
        this.browserService.isOnMobile() &&
        this.user?.preferences?.openMobileAddByDefault &&
        !this.isInitialized
      ) {
        this.mainState = 'mobile-adder';
      }

      this.isInitialized = true;
    }
  }

  private addMissingParentTags(tags: Tag[]): Tag[] {
    let tagsToReturn = [...tags];
    tags.forEach((tag) => {
      if (!this.hasDirectParent(tags, tag) && !isRoot(tag.fullPath)) {
        tagsToReturn = this.addDirectParent(tags, tag);
      }
    });

    return tagsToReturn;
  }

  private hasDirectParent(tags: Tag[], tag: Tag): boolean {
    return tags.some((t) => isDirectParent(t.fullPath, tag.fullPath));
  }

  private addDirectParent(tags: Tag[], tag: Tag): Tag[] {
    const parentFullPath = tag.fullPath.split('/').slice(0, -1).join('/');

    const parent = tags.find((t) => t.fullPath === parentFullPath);
    if (!parent && this.tags) {
      const tagToAdd = {
        fullPath: parentFullPath,
        color: TagService.DEFAULT_COLOR,
        isArchived: tag.isArchived,
        isPeriodic: tag.isPeriodic,
      };

      tags.push(tagToAdd);
    }
    return tags;
  }

  private orderTags(user: User, tags: Tag[]) {
    if (user?.tagIdsInPreferredOrder?.length) {
      this.tags = applyPreferredTagOrder(user.tagIdsInPreferredOrder, tags);
    }
  }

  private mapDetails(tags: Tag[], entries: Entry[]) {
    const { tagNodes, tagsWithSummaryDetails, detailedEntries } =
      TagTreeMapper.mapDetails(tags, entries, {
        defaultColor: TagService.DEFAULT_COLOR,
        defaultSummary: Summary.none,
      });
    this.tagNodes = tagNodes;
    this.detailedTags = tagsWithSummaryDetails.filter((t) => !t.isArchived);

    this.detailedArchivedTags = tagsWithSummaryDetails.filter(
      (t) => t.isArchived
    );
    this.detailedEntries = detailedEntries;
  }

  async deleteEntry(entry: Entry) {
    await this.entryService.delete(entry.id!);
    this._snackBar.open(`🔥 Entry deleted ${entry.amount} for ${entry.memo}`);
  }

  async editEntry(entry: Entry) {
    this.openAdder(entry);
  }

  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (
      !this.dialogService.hasOpen &&
      !this.showCover &&
      !this.browserService.isOnMobile()
    ) {
      switch (event.key) {
        case 'a':
        case 'A':
          this.router.navigate(['/all']);
          break;
        case 'n':
        case 'N':
          this.openAdder();
          break;
        case 'p':
        case 'P':
          this.openPlanner();
          break;
        case 's':
        case 'S':
          this.openSettings();
          break;
      }
    }
  }

  openAllEntries() {
    this.openEntriesWithTag('');
  }

  openEntriesWithTag(
    tagFullPath: string,
    period?: Period,
    preventSidebarClose?: boolean
  ) {
    this.tagFullPath = tagFullPath;
    this.periodFilter = period ?? null;
    this.mainState = 'entries';
    this.location.replaceState(`/tags/${tagFullPath}`);
    if (this.browserService.isOnMobile() && !preventSidebarClose) {
      this.sidenav?.close();
    }
  }

  openPlannerMobile() {
    this.sidenav.close();
    this.mainState = 'mobile-planner';
  }

  openPlannerDesktop(autoFocusTagId?: string) {
    this.dialogService.open(
      PlannerComponent,
      {
        tags: this.detailedTags,
        archivedTags: this.detailedArchivedTags,
        autoFocusTagId,
      },
      'planner-dialog',
      true,
      (data: any) => {
        if (data?.sidebarShouldRefresh) {
          this.sidebar.hardRefresh();
        }
        if (data?.openUpsell) {
          this.openSettingsDesktop('prefund_pro');
        }
      }
    );
  }

  openPlanner(autoFocusTagId?: string) {
    if (this.browserService.isOnMobile()) {
      this.openPlannerMobile();
    } else {
      this.openPlannerDesktop(autoFocusTagId);
    }
  }

  openAdderMobile() {
    if (this.mobileAdder) {
      this.mobileAdder.clear();
    }
    if (this.tagFullPath) {
      this.tagSelectionService.selectedTags = [
        this.detailedTags.find((t) => t.fullPath === this.tagFullPath)!,
      ];
    } else {
      this.tagSelectionService.selectedTags = [];
    }
    this.mainState = 'mobile-adder';
    this.sidenav.close();
  }

  openAdderDesktop(entry?: Entry) {
    this.dialogService.open(
      AddComponent,
      {
        data: {
          tag: this.tagFullPath,
          tags: this.detailedTags,
          entry,
        },
      },
      'add-entry-dialog',
      false
    );
  }

  openAdder(entry?: Entry) {
    if (this.browserService.isOnMobile()) {
      this.openAdderMobile();
    } else {
      this.openAdderDesktop(entry);
    }
  }

  openSubscriptionSuccessModal(sessionId: string) {
    this.dialogService.open(
      SubscriptionSuccessDialogComponent,
      {
        data: {
          sessionId,
        },
      },
      'subscription-success-dialog',
      true
    );
  }

  coverInitialState: 'login' | 'signup' | 'hero' = 'hero';

  openHero() {
    this.coverInitialState = 'hero';
    this.showCover = true;
  }

  openLogin() {
    this.coverInitialState = 'login';
    this.showCover = true;
  }

  openSignup() {
    this.coverInitialState = 'signup';
    this.showCover = true;
  }

  openSettings(state?: string) {
    this.showCover = false;
    if (this.browserService.isOnMobile()) {
      this.openSettingsMobile(state);
    } else {
      this.openSettingsDesktop(state);
    }
  }

  mobileSettingsState = 'preferences';

  openSettingsMobile(state = 'menu') {
    this.mobileSettingsState = state;
    this.mainState = 'mobile-settings';
    this.sidenav.close();
  }

  openSettingsDesktop(state = 'preferences') {
    this.dialogService.open(
      SettingsComponent,
      { state },
      'settings-dialog',
      false,
      (data: any) => {
        if (data?.shouldOpenLogin) {
          this.openLogin();
        }
      },
      '800px',
      '700px'
    );
  }

  showEntries() {
    this.mainState = 'entries';
    if (this.browserService.isOnMobile()) {
      this.sidenav.close();
    }
  }
}
